import * as constants from '../constants';
import {
    EB_14_DAY_TRIAL_250_PACKAGE,
    EB_14_DAY_TRIAL_UNLIM_PACKAGE,
    EB_60_DAY_TRIAL_UNLIM_PACKAGE,
    PRO_250_TRIAL_PLAN_NAME,
    PRO_UNLIMITED_TRIAL_PLAN_NAME,
    TRIAL_PLAN_NAME,
    TRIAL_STATUS_ACTIVE,
} from '../constants';
import { SubscriptionObject } from '../subscriptions';
import { PPTrial } from '../trials';
import { PricingAndPackagingFeatureFlags } from '../types';
import { getISODate, getPlanPricingObject } from '../utils';
import {
    EmptyPlan,
    FuturePlan,
    PlanDetailsObject,
    PlanOption,
    PlanTier,
    SubscriptionAndTrialStatus,
} from './types';

export const planTierToNameMap: Record<PlanTier, string> = {
    [constants.DEPRECATED_BOOST_PLAN]: constants.DEPRECATED_BOOST_PLAN_NAME,
    [constants.LITE]: constants.PRO_100_PLAN_NAME,
    [constants.CORE]: constants.PRO_250_PLAN_NAME,
    [constants.PRO]: constants.PRO_UNLIMITED_PLAN_NAME,
    [constants.ADMIN_LITE]: constants.PRO_100_PLAN_NAME,
    [constants.ADMIN_CORE]: constants.PRO_250_PLAN_NAME,
    [constants.ADMIN_PRO]: constants.PRO_UNLIMITED_PLAN_NAME,
    [constants.MIGRATION_PRO]: constants.PRO_UNLIMITED_PLAN_NAME,
    [constants.NPO_LITE]: constants.PRO_100_PLAN_NAME,
    [constants.NPO_CORE]: constants.PRO_250_PLAN_NAME,
    [constants.NPO_PRO]: constants.PRO_UNLIMITED_PLAN_NAME,
};

export const interimPricingAndPackagingPlanTierToNameMap: Record<
    PlanTier,
    string
> = {
    [constants.DEPRECATED_BOOST_PLAN]: constants.DEPRECATED_BOOST_PLAN_NAME,
    [constants.LITE]: constants.PRO_2K_PLAN_NAME,
    [constants.CORE]: constants.PRO_6K_PLAN_NAME,
    [constants.PRO]: constants.PRO_10K_PLAN_NAME,
    [constants.ADMIN_LITE]: constants.PRO_2K_PLAN_NAME,
    [constants.ADMIN_CORE]: constants.PRO_6K_PLAN_NAME,
    [constants.ADMIN_PRO]: constants.PRO_10K_PLAN_NAME,
    [constants.MIGRATION_PRO]: constants.PRO_10K_PLAN_NAME,
    [constants.NPO_LITE]: constants.PRO_2K_PLAN_NAME,
    [constants.NPO_CORE]: constants.PRO_6K_PLAN_NAME,
    [constants.NPO_PRO]: constants.PRO_10K_PLAN_NAME,
};

export const planTierToCapacityMap: Record<PlanTier, number> = {
    [constants.DEPRECATED_BOOST_PLAN]: 0,
    [constants.LITE]: constants.PRO_100_CAPACITY_MAX,
    [constants.CORE]: constants.PRO_250_CAPACITY_MAX,
    [constants.PRO]: constants.EVENT_CAPACITY_MAX,
    [constants.ADMIN_LITE]: constants.PRO_100_CAPACITY_MAX,
    [constants.ADMIN_CORE]: constants.PRO_250_CAPACITY_MAX,
    [constants.ADMIN_PRO]: constants.EVENT_CAPACITY_MAX,
    [constants.MIGRATION_PRO]: constants.EVENT_CAPACITY_MAX,
    [constants.NPO_LITE]: constants.PRO_100_CAPACITY_MAX,
    [constants.NPO_CORE]: constants.PRO_250_CAPACITY_MAX,
    [constants.NPO_PRO]: constants.EVENT_CAPACITY_MAX,
};

export const planNameToTierMap: Record<string, PlanTier> = {
    [constants.PRO_100_PLAN_NAME]: constants.LITE,
    [constants.PRO_250_PLAN_NAME]: constants.CORE,
    [constants.PRO_UNLIMITED_PLAN_NAME]: constants.PRO,
    [constants.NPO_PRO_100_PLAN_NAME]: constants.NPO_LITE,
    [constants.NPO_PRO_250_PLAN_NAME]: constants.NPO_CORE,
    [constants.NPO_PRO_UNLIMITED_PLAN_NAME]: constants.NPO_PRO,
};

export const interimPricingAndPackagingPlanNameToTierMap: Record<
    string,
    PlanTier
> = {
    [constants.PRO_2K_PLAN_NAME]: constants.LITE,
    [constants.PRO_6K_PLAN_NAME]: constants.CORE,
    [constants.PRO_10K_PLAN_NAME]: constants.PRO,
    [constants.NPO_PRO_2K_PLAN_NAME]: constants.NPO_LITE,
    [constants.NPO_PRO_6K_PLAN_NAME]: constants.NPO_CORE,
    [constants.NPO_PRO_10K_PLAN_NAME]: constants.NPO_PRO,
};

export const planTierToDailyLimitMap: Record<PlanTier, number> = {
    [constants.DEPRECATED_BOOST_PLAN]: 0,
    [constants.LITE]: constants.PRO_100_CAMPAIGN_DAILY_LIMIT,
    [constants.CORE]: constants.PRO_250_CAMPAIGN_DAILY_LIMIT,
    [constants.PRO]: constants.PRO_UNLIMITED_CAMPAIGN_DAILY_LIMIT,
    [constants.ADMIN_LITE]: constants.PRO_100_CAMPAIGN_DAILY_LIMIT,
    [constants.ADMIN_CORE]: constants.PRO_250_CAMPAIGN_DAILY_LIMIT,
    [constants.ADMIN_PRO]: constants.PRO_UNLIMITED_CAMPAIGN_DAILY_LIMIT,
    [constants.MIGRATION_PRO]: constants.PRO_UNLIMITED_CAMPAIGN_DAILY_LIMIT,
    [constants.NPO_LITE]: constants.PRO_100_CAMPAIGN_DAILY_LIMIT,
    [constants.NPO_CORE]: constants.PRO_250_CAMPAIGN_DAILY_LIMIT,
    [constants.NPO_PRO]: constants.PRO_UNLIMITED_CAMPAIGN_DAILY_LIMIT,
};

/**
 * Retrieves detailed plan information for an organization based on the provided plan and subscription.
 * @param {PlanOption} plan - The current plan option, possibly an empty object.
 * @param {SubscriptionObject} subscription - The current subscription object, possibly an empty object.
 * @param {string} currency - The currency in which the plan is billed.
 * @param {Object} options - An object containing optional parameters.
 * @returns {PlanDetailsObject | null} An object containing the plan details, including name, pricing, and status.
 */
export const getOrganizationPlanDetails = (
    plan: PlanOption | EmptyPlan,
    subscription: SubscriptionObject,
    currency: string,
    options?: { featureFlags?: PricingAndPackagingFeatureFlags },
): PlanDetailsObject | null => {
    const isTrialing =
        plan?.trial?.status === constants.SUBSCRIPTION_STATUS_TRIALING;

    const isActiveSubscription =
        subscription?.status === constants.SUBSCRIPTION_STATUS_ACTIVE;

    const planBillingPeriodInterval = isTrialing
        ? 'month'
        : plan?.pricing?.[0]?.period?.interval;

    const pricingObject = getPlanPricingObject(
        plan as PlanOption,
        currency,
        planBillingPeriodInterval,
    );

    const priceDisplay = pricingObject?.currency_options?.[
        currency.toLowerCase()
    ]
        ? pricingObject.currency_options[currency.toLowerCase()].value
        : pricingObject?.currency_options['usd'].value;

    // If a subscription is past due, canceled or incomplete, subscription service returns an empty object for the org's plan
    // In this case, we shouldn't return null but just the plan details minus plan specific details
    if (
        subscription &&
        [
            constants.SUBSCRIPTION_STATUS_DELINQUENT,
            constants.SUBSCRIPTION_STATUS_CANCELED,
            constants.SUBSCRIPTION_STATUS_INCOMPLETE,
        ].includes(subscription.status)
    ) {
        const futurePlan =
            subscription.future_phases?.length &&
            subscription.status !== constants.SUBSCRIPTION_STATUS_CANCELED
                ? subscription.future_phases?.[0]
                : null;

        if (futurePlan) {
            futurePlan.plan_name = getPlanNameFromPlanTier(
                futurePlan.plan_tier,
                { featureFlags: options?.featureFlags },
            );
        }

        const eventCapacity = planTierToCapacityMap[subscription.plan_tier];

        const planName = getPlanNameFromPlanTier(subscription.plan_tier, {
            featureFlags: options?.featureFlags,
        });

        const planDetails: PlanDetailsObject = {
            features: plan?.features || {
                event_capacity:
                    eventCapacity == constants.EVENT_CAPACITY_MAX
                        ? 'unlimited'
                        : eventCapacity.toString(),
                campaign_daily_limit:
                    planTierToDailyLimitMap[subscription.plan_tier].toString(),
            },
            plan_name: planName,
            plan_tier: subscription.plan_tier,
            plan_id: subscription.plan_id,
            plan_price_id: subscription.plan_price_id,
            price: null,
            currency,
            created_at: subscription.created_at,
            status: subscription.status,
            future_phases: futurePlan,
            latest_invoice: subscription.latest_invoice,
        };

        // it's possible that an organization that has a subscription in this state gets to trial a plan.
        // in that case, we should build the plan details to include it.

        // the incomplete state should take precedence over the trial state
        if (
            isTrialing &&
            subscription.status !== constants.SUBSCRIPTION_STATUS_INCOMPLETE
        ) {
            // if an organization has an active trial, they will have a plan object
            const planName = getPlanNameFromPlanTier(plan.plan_tier, {
                featureFlags: options?.featureFlags,
            });

            Object.assign(planDetails, {
                trial_expiration_date: plan?.trial?.ends_at,
                status: constants.SUBSCRIPTION_STATUS_TRIALING,
                interval: 'month',
                plan_tier: plan?.plan_tier,
                plan_id: plan?.plan_id,
                plan_name: planName,
                plan_price_id: plan?.trial?.plan_price_id || '',
                price: priceDisplay,
                created_at: plan?.trial?.created_at
                    ? new Date(plan?.trial?.created_at).getTime()
                    : undefined,
            });
        }

        return planDetails;
    }

    // criteria:
    // - plan is empty or subscription is empty and org is not on a trial
    // - canceled attempt to subscribe when subscription was in the verification of payment method state
    const isOnFlexPlan =
        !Object.keys(plan).length ||
        (!Object.keys(subscription).length && !plan?.trial) ||
        (subscription.status ===
            constants.SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED &&
            !plan?.trial);

    if (isOnFlexPlan) {
        return null;
    }

    // if the pricing object doesn't exist, what are we building the plan details on? return null and log the error
    if (!pricingObject || priceDisplay == undefined) {
        return null;
    }

    const hasActiveTrialWithExpiredIncompleteSubscription =
        isTrialing &&
        subscription.status ===
            constants.SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED;

    const createdAt =
        hasActiveTrialWithExpiredIncompleteSubscription && plan.trial
            ? new Date(plan.trial.created_at).getTime()
            : subscription.created_at;
    const status = hasActiveTrialWithExpiredIncompleteSubscription
        ? constants.SUBSCRIPTION_STATUS_TRIALING
        : subscription.status || plan.trial?.status;

    const planName = getPlanNameFromPlanTier(plan.plan_tier, {
        featureFlags: options?.featureFlags,
    });

    const basePlanDetails = {
        plan_id: plan.plan_id,
        plan_name: planName,
        plan_price_id: pricingObject?.plan_price_id,
        plan_tier: plan.plan_tier,
        features: plan.features,
        price: priceDisplay,
        currency: currency,
        created_at: createdAt,
        interval: pricingObject?.period.interval,
    };

    const hasScheduledCancelation =
        subscription.cancel_at_period_end || Boolean(subscription.cancels_at);

    const createdAtDate = getISODate(createdAt);
    const cancelsAtDate = subscription.cancels_at
        ? getISODate(subscription.cancels_at)
        : null;
    const nextPaymentDate = isActiveSubscription
        ? getISODate(subscription.current_period_end)
        : undefined;
    const expirationDate = hasScheduledCancelation
        ? getISODate(subscription.cancels_at)
        : null;
    const trialExpirationDate = plan.trial ? plan.trial.ends_at : null;
    const futurePlan = subscription.future_phases?.length
        ? subscription.future_phases?.[0]
        : null;

    if (futurePlan) {
        futurePlan.plan_name = getPlanNameFromPlanTier(futurePlan.plan_tier, {
            featureFlags: options?.featureFlags,
        });
    }

    return {
        ...basePlanDetails,
        billing_cycle_date: nextPaymentDate,
        created_at_date: createdAtDate,
        next_invoice_amount:
            isActiveSubscription && hasScheduledCancelation
                ? null
                : basePlanDetails.price,
        next_payment_date: nextPaymentDate,
        expiration_date: expirationDate,
        cancels_at: cancelsAtDate,
        trial_expiration_date: trialExpirationDate,
        status,
        future_phases: futurePlan,
        latest_invoice: subscription.latest_invoice,
    };
};

/**
 * Determines the subscription and trial status of an organization.
 * @param {Object} params - An object containing a subscription and trial.
 * @param {Partial<SubscriptionObject>} params.subscription - The current subscription object, if any.
 * @param {Partial<PPTrial> | null} params.trial - The current trial object, if any.
 * @returns {SubscriptionAndTrialStatus} An object indicating various statuses related to subscription and trial.
 */
export const getSubscriptionAndTrialStatus = ({
    subscription,
    trial,
    options,
}: {
    subscription?: Partial<SubscriptionObject>;
    trial?: Partial<PPTrial> | null;
    options?: { featureFlags?: PricingAndPackagingFeatureFlags };
}): SubscriptionAndTrialStatus => {
    const is_subscribed =
        subscription?.status === constants.SUBSCRIPTION_STATUS_ACTIVE;

    const cancelAtPeriodEndIsSet =
        subscription?.cancel_at_period_end || Boolean(subscription?.cancels_at);

    const is_subscription_set_to_cancel =
        is_subscribed && cancelAtPeriodEndIsSet;

    const is_trialing =
        trial?.status === constants.SUBSCRIPTION_STATUS_TRIALING &&
        !is_subscribed;

    const is_trial_eligible = !trial && !is_subscribed;

    const has_opted_out_of_auto_subscribing = trial?.should_auto_sub === 'N';

    const is_post_trial = !!(trial?.status === constants.TRIAL_STATUS_ENDED);

    const is_past_due =
        subscription?.status === constants.SUBSCRIPTION_STATUS_DELINQUENT;

    const has_initiated_extended_trial =
        trial?.package_id === EB_60_DAY_TRIAL_UNLIM_PACKAGE;

    const trial_plan_name =
        trial?.package_id === EB_60_DAY_TRIAL_UNLIM_PACKAGE ||
        trial?.package_id === EB_14_DAY_TRIAL_UNLIM_PACKAGE
            ? PRO_UNLIMITED_TRIAL_PLAN_NAME
            : trial?.package_id === EB_14_DAY_TRIAL_250_PACKAGE
            ? PRO_250_TRIAL_PLAN_NAME
            : TRIAL_PLAN_NAME;

    let trial_plan_tier: PlanTier = constants.LITE;

    if (trial?.package_id) {
        trial_plan_tier = [
            EB_60_DAY_TRIAL_UNLIM_PACKAGE,
            EB_14_DAY_TRIAL_UNLIM_PACKAGE,
        ].includes(trial.package_id)
            ? constants.PRO
            : trial?.package_id === EB_14_DAY_TRIAL_250_PACKAGE
            ? constants.CORE
            : constants.LITE;
    }

    const pro_plan_tier = getPlanTierFromPlanId(trial?.plan_id);
    const pro_plan_name = getPlanNameFromPlanTier(pro_plan_tier, options);

    return {
        is_subscribed,
        is_subscription_set_to_cancel,
        is_trialing,
        is_trial_eligible,
        is_past_due,
        has_opted_out_of_auto_subscribing,
        is_post_trial,
        has_initiated_extended_trial,
        pro_plan_post_trial: {
            plan_name: pro_plan_name,
            plan_tier: pro_plan_tier,
        },
        trial_plan_name,
        trial_plan_tier,
        trial_duration: constants.STANDARD_TRIAL_DURATION_DAYS,
        trial_package_id:
            trial?.package_id || constants.EB_14_DAY_TRIAL_100_PACKAGE,
    };
};

/**
 * Retrieves the plan name based on the provided plan option.
 * @param {PlanOption} plan - The plan option.
 * @param {PricingAndPackagingFeatureFlags} featureFlags - Optional feature flags.
 * @returns {string} The plan name.
 */
export const getPlanName = (
    plan: PlanOption,
    featureFlags?: PricingAndPackagingFeatureFlags,
): string => {
    if (plan.trial?.status === TRIAL_STATUS_ACTIVE) {
        return constants.TRIAL_PLAN_NAME;
    }

    return getPlanNameFromPlanTier(plan.plan_tier, { featureFlags });
};

/**
 * Retrieves the plan tier based on the provided plan ID.
 * @param {string} planId - The plan ID.
 * @returns {PlanTier} The plan tier.
 */
export const getPlanTierFromPlanId = (planId?: string): PlanTier => {
    if (!planId) return constants.LITE;
    if (planId?.includes(constants.LITE)) {
        return constants.LITE;
    } else if (planId?.includes(constants.CORE)) {
        return constants.CORE;
    } else return constants.PRO;
};

/**
 * Retrieves the plan name based on the provided plan ID.
 * @param {string} planId - The plan ID.
 * @param {object} options - Optional parameters.
 * @returns {string} The plan name.
 */
export const getPlanNameFromPlanId = (
    planId?: string,
    options?: { featureFlags?: PricingAndPackagingFeatureFlags },
): string => {
    const planTier = getPlanTierFromPlanId(planId);
    return getPlanNameFromPlanTier(planTier, {
        featureFlags: options?.featureFlags,
    });
};

/**
 * Retrieves the plan tier based on the provided plan name.
 * @param {string} planName - The plan name.
 * @param {object} options - Optional parameters.
 * @returns {PlanTier} The plan tier.
 */
export const getPlanTierFromName = (
    planName: string,
    options?: {
        featureFlags?: PricingAndPackagingFeatureFlags;
        isNPO?: boolean;
    },
): PlanTier => {
    const { enableRemovePricingAndPackaging } = options?.featureFlags || {};

    const _planName = options?.isNPO ? `NPO ${planName}` : planName;

    return enableRemovePricingAndPackaging
        ? interimPricingAndPackagingPlanNameToTierMap[_planName]
        : planNameToTierMap[_planName];
};

/**
 * Translates the plan name for P&P rollback.
 * @param {string} oldPlanName - The plan name to be translated.
 * @param {boolean} enableRemovePricingAndPackaging - Flag indicating whether to enable the removal of pricing and packaging.
 * @returns {string} The new interim plan name if oldPlanName is a valid old plan name, otherwise it returns the passed in parameter.
 */
export const translateOldPlanNameForPAndPRollback = (
    oldPlanName: string,
    enableRemovePricingAndPackaging: boolean,
): string => {
    const isOldPlanName =
        Object.values(planTierToNameMap).includes(oldPlanName);

    if (enableRemovePricingAndPackaging && isOldPlanName) {
        const planTier = getPlanTierFromName(oldPlanName);
        return interimPricingAndPackagingPlanTierToNameMap[planTier];
    }

    return oldPlanName;
};

/**
 * Retrieves the plan name based on the provided plan tier.
 * @param {PlanTier} planTier - The plan tier.
 * @param {object} options - Optional parameters.
 * @returns {string} The plan name.
 */
export const getPlanNameFromPlanTier = (
    planTier: PlanTier,
    options?: { featureFlags?: PricingAndPackagingFeatureFlags },
): string => {
    const { enableRemovePricingAndPackaging } = options?.featureFlags || {};

    return enableRemovePricingAndPackaging
        ? interimPricingAndPackagingPlanTierToNameMap[planTier]
        : planTierToNameMap[planTier];
};

/**
 * Checks if the plan tier is a Lite plan.
 * @param {PlanTier} planTier - The plan tier.
 * @returns {boolean} True if the plan tier is a Lite plan, false otherwise.
 */
export const isLitePlan = (planTier?: PlanTier): boolean => {
    if (!planTier) return false;
    return [constants.LITE, constants.ADMIN_LITE, constants.NPO_LITE].includes(
        planTier,
    );
};

/**
 * Checks if the plan tier is a Core plan.
 * @param {PlanTier} planTier - The plan tier.
 * @returns {boolean} True if the plan tier is a Core plan, false otherwise.
 */
export const isCorePlan = (planTier?: PlanTier) => {
    if (!planTier) return false;
    return [constants.CORE, constants.ADMIN_CORE, constants.NPO_CORE].includes(
        planTier,
    );
};

/**
 * Checks if the plan is an admin plan.
 * @param {PlanOpton} plan - The plan.
 * @returns {boolean} True if the plan is an admin plan, false otherwise.
 */
export const isAdminPlan = (plan?: PlanOption | PlanDetailsObject | null) => {
    if (!plan?.plan_tier) return false;
    return constants.ADMIN_PLANS.includes(plan.plan_tier);
};

/**
 * Checks if the plan is an npo plan.
 * @param {PlanOption} plan - The plan.
 * @returns {boolean} True if the plan is an npo plan, false otherwise.
 */
export const isNPOPlan = (plan?: PlanOption | PlanDetailsObject | null) => {
    if (!plan?.plan_tier) return false;
    return constants.NPO_PLANS.includes(plan.plan_tier);
};

/**
 * Checks if the plan is a free plan.
 * @param {PlanOption} plan - The plan.
 * @returns {boolean} True if the plan is a free plan, false otherwise.
 */
export const isFreePlan = (plan?: PlanDetailsObject | null) => {
    return plan == null || plan == undefined;
};

/**
 * Checks if the plan is a regular plan.
 * @param {PlanOption} plan - The plan tier.
 * @returns {boolean} True if the plan tier is a regular plan, false otherwise.
 */
export const isRegularPlan = (plan?: PlanOption | PlanDetailsObject | null) => {
    if (!plan?.plan_tier) return false;
    return constants.REGULAR_PLANS.includes(plan.plan_tier);
};

/**
 * Checks if the plan tier is a Pro plan.
 * @param {PlanTier} planTier - The plan tier.
 * @returns {boolean} True if the plan tier is a Pro plan, false otherwise.
 */
export const isProPlan = (planTier?: PlanTier) => {
    if (!planTier) return false;
    return [
        constants.PRO,
        constants.ADMIN_PRO,
        constants.MIGRATION_PRO,
        constants.NPO_PRO,
    ].includes(planTier);
};

/**
 * Checks if two plans are the same (based on their tiers).
 * @param {PlanOption | PlanDetailsObject | FuturePlan} plan1 - The first plan.
 * @param {PlanOption | PlanDetailsObject | FuturePlan} plan2 - The second plan.
 * @returns {boolean} True if the plan tiers are the same, false otherwise.
 */
export const areIdenticalPlans = (
    plan1?: PlanOption | PlanDetailsObject | FuturePlan | null,
    plan2?: PlanOption | PlanDetailsObject | FuturePlan | null,
) => {
    if (!plan1 || !plan2) return false;
    return plan1?.plan_tier === plan2?.plan_tier;
};

/**
 * Checks if two plan tiers are similar (eg. Lite and NPO Lite, Core and Admin Core, etc.)
 * @param {PlanTier} planTier1 - The first plan tier.
 * @param {PlanTier} planTier2 - The second plan tier.
 * @returns {boolean} True if the plan tiers are the same, false otherwise.
 */
export const areSimilarTierPlans = (
    planTier1?: PlanTier,
    planTier2?: PlanTier,
) => {
    const isLite = isLitePlan(planTier1) && isLitePlan(planTier2);
    const isCore = isCorePlan(planTier1) && isCorePlan(planTier2);
    const isPro = isProPlan(planTier1) && isProPlan(planTier2);

    return isLite || isCore || isPro;
};

/**
 * Gets the next plan tier based on the current plan tier.
 * @param {PlanTier} currentPlanTier - The current plan tier.
 * @returns {PlanTier} The next plan tier.
 */
export const getNextPlanTier = (currentPlanTier: PlanTier): PlanTier => {
    switch (currentPlanTier) {
        case constants.LITE:
            return constants.CORE;

        case constants.ADMIN_LITE:
            return constants.ADMIN_CORE;

        case constants.NPO_LITE:
            return constants.NPO_CORE;

        case constants.CORE:
            return constants.PRO;

        case constants.ADMIN_CORE:
            return constants.ADMIN_PRO;

        case constants.NPO_CORE:
            return constants.NPO_PRO;

        default:
            return constants.PRO;
    }
};
